import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Plane, MapPin, Clock, Shield, Users, Car, ChevronRight, Phone, Mail, Globe, Star,
  CheckCircle2, Building2, Briefcase, Heart, Ship, Landmark, Mountain
} from "lucide-react";
import PageTransition from "../../components/PageTransition";

const SydneyPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Schema markup for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Limo Near Me - Sydney",
    "description": "Sydney luxury chauffeur services for airport transfers, harbour cruises, corporate travel and special events. Premium fleet available 24/7.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Sydney",
      "addressRegion": "NSW",
      "addressCountry": "AU"
    },
    "telephone": "+61481437772",
    "email": "info@limonearme.com.au",
    "url": "https://www.limonearme.com.au/chauffeur/sydney",
    "priceRange": "$$-$$$",
    "openingHours": "Mo-Su 00:00-23:59",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "156"
    },
    "areaServed": {
      "@type": "City",
      "name": "Sydney"
    }
  };

  const services = [
    { 
      icon: Plane, 
      title: "Sydney Airport Limo Transfers", 
      desc: "Our chauffeur-driven service ensures smooth transfers to/from Sydney Airport (SYD). Enjoy punctual arrival times, comfy ride experiences, and courteous service from highly trained chauffeurs."
    },
    { 
      icon: Briefcase, 
      title: "Corporate Transfers & Business Hire", 
      desc: "Executive transfers and business hourly hire throughout Sydney CBD, North Sydney, Parramatta, and all business districts."
    },
    { 
      icon: Ship, 
      title: "Cruise Terminal Transfers", 
      desc: "Luxury transfers to Sydney's Circular Quay and White Bay cruise terminals. Luggage handling included for seamless embarkation."
    },
    { 
      icon: Mountain, 
      title: "Blue Mountains & Hunter Valley Tours", 
      desc: "Chauffeur-driven tours to the Blue Mountains, Hunter Valley wineries, and iconic Sydney attractions."
    }
  ];

  const serviceHighlights = [
    "Private airport pickups & meet-and-greet services",
    "Corporate transfers and business hourly hire",
    "Event transportation for weddings, functions, and celebrations",
    "Fleet options from executive sedans to luxury SUVs",
    "Flight tracking with automatic delay adjustments",
    "24/7 availability and instant booking"
  ];

  const popularRoutes = [
    { from: "Sydney Airport (SYD)", to: "Sydney CBD", price: "From $95" },
    { from: "Sydney Airport (SYD)", to: "North Sydney", price: "From $110" },
    { from: "Sydney Airport (SYD)", to: "Parramatta", price: "From $130" },
    { from: "Sydney CBD", to: "Blue Mountains tour", price: "From $550" },
    { from: "Sydney CBD", to: "Hunter Valley tour", price: "From $800" },
    { from: "Sydney Airport (SYD)", to: "Bondi Beach", price: "From $110" }
  ];

  const suburbs = [
    "Sydney CBD", "North Sydney", "Parramatta", "Bondi", "Surry Hills",
    "Darling Harbour", "Circular Quay", "Chatswood", "Manly", "Mosman",
    "Double Bay", "Paddington", "Newtown", "Pyrmont", "Barangaroo"
  ];

  return (
    <PageTransition>
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/limo-logo.jpg" alt="Limo Near Me Sydney" className="h-12 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-muted-foreground hover:text-foreground transition-colors">Services</Link>
              <Link to="/#fleet" className="text-muted-foreground hover:text-foreground transition-colors">Fleet</Link>
              <Link to="/#locations" className="text-muted-foreground hover:text-foreground transition-colors">Locations</Link>
              <Link to="/#contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+61481437772" className="hidden sm:flex items-center gap-2 text-primary">
                <Phone className="h-4 w-4" />
                <span className="font-semibold">+61 481 437 772</span>
              </a>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* SEO Helmet */}
      <Helmet>
        <title>Sydney Limo Service | Airport Transfers & Chauffeur Hire</title>
        <meta name="description" content="Sydney luxury chauffeur services for airport transfers, harbour cruises, corporate travel and special events. Premium fleet available 24/7. Book now." />
        <meta name="keywords" content="Sydney limo, Sydney chauffeur, Sydney airport transfer, corporate travel Sydney, harbour cruise transfer, SYD airport limo" />
        <link rel="canonical" href="https://www.limonearme.com.au/chauffeur/sydney" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1920&h=1080&fit=crop')",
          }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 hero-glow" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="initial" animate="animate" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <MapPin className="h-4 w-4" />
              <span className="text-sm font-medium">Sydney, New South Wales</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground mb-6">
              Sydney Limo Service<br />
              <span className="text-primary">& Airport Transfers</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Step into luxury with premium limo transportation across Sydney — perfect for corporate travellers, 
              tourists, and special occasions. Experience Sydney's best executive limo service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold glow-primary"
                >
                  Get Sydney Quote <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-lg border-black/20 hover:bg-black/5"
                >
                  <Phone className="mr-2 h-5 w-5" /> Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sydney Limo & Chauffeur Services */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Sydney Limo & Chauffeur Services Include
              </h2>
              <p className="text-muted-foreground mb-6">
                Whether it's Sydney CBD, North Sydney, Parramatta, or Bondi, travel in comfort, style and 
                professionalism with Sydney's premier limo service.
              </p>
              <ul className="space-y-4 mb-8">
                {serviceHighlights.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Book Sydney Limo <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=600&h=400&fit=crop" 
                alt="Sydney Airport Limo Transfer"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-xl border border-black/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Plane className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">Sydney Airport</p>
                    <p className="text-muted-foreground text-sm">SYD - All Terminals</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Executive Limo Services in Sydney</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive luxury limo and chauffeur solutions across Sydney and NSW
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card group hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Sydney Limo Transfer Routes</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Fixed-price limo transfers from Sydney Airport and CBD
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularRoutes.map((route, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card hover:border-primary/50 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-foreground font-medium">{route.from}</span>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span className="text-foreground font-medium">{route.to}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-black/10">
                  <Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Get Price</Button></Link>
                  <Link to="/book">
                    <Button size="sm" variant="outline" className="rounded-full">
                      Book <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Sydney Limo Service Areas</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Limo services across Greater Sydney and beyond
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-3"
          >
            {suburbs.map((suburb, index) => (
              <span 
                key={index}
                className="px-4 py-2 rounded-full bg-card border border-black/10 text-muted-foreground text-sm hover:border-primary/50 hover:text-primary transition-all cursor-default"
              >
                {suburb}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Book Your Sydney Limo Today
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience luxury on every journey with Sydney's premier limo service. Get an instant quote or call our 24/7 booking team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-bold glow-primary"
                >
                  Get a Quote <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-10 py-6 text-lg border-primary/50 hover:bg-black/5"
                >
                  <Phone className="mr-2 h-5 w-5" /> +61 481 437 772
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <SeoFooter />
    </div>
    </PageTransition>
  );
};

export default SydneyPage;
